/*! mdv-recs.js - Maniere De Voir cross-sell module.  MARKER: mdvRecsV1
 *
 *  Phase 3, 2026-08-21.  Replaces the legacy Liquid recommendation scanner
 *  (templates/product.recommendation-product.liquid) on the PDP.
 *
 *  Primary tier  : mdv-search-v2 Worker, GET /v2/recs/{STYLE_ROOT}
 *  Fallback tier : /collections/{womens|mens}-best-sellers/products.json
 *
 *  Fails open at every step: any network error, timeout, non-2xx or malformed
 *  payload is treated as "no candidates", which drops through to the
 *  best-sellers ladder and finally hides the section entirely.  The module
 *  must never leave an empty frame on the page.
 *
 *  Two presenters live here.  Only the PDP presenter is wired up in this
 *  phase; mountCart() is complete but is deliberately never invoked.
 */
(function (factory) {
  'use strict';
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (typeof window !== 'undefined') {
    window.MdvRecsV1 = api;
    if (typeof document !== 'undefined') {
      api.autoInit();
    }
  }
})(function () {
  'use strict';

  /* Distinctive ASCII marker - survives Shopify CDN minification and is the
     string used by the post-publish grep set. */
  var MARKER = 'mdvRecsV1';

  var RECS_ENDPOINT = 'https://mdv-search-v2.reece-c61.workers.dev/v2/recs/';
  var FETCH_TIMEOUT_MS = 2500;
  var MAX_TILES = 8;
  var MIN_TILES = 4;
  var MAX_MARKDOWN = 0.31; /* universal 31% markdown cap - exclude when strictly greater */
  var CONCURRENCY = 6;

  /*  Tile density matches the legacy module: 6 across on desktop, 4 on
   *  tablet, 3 on mobile.  A desktop tile is therefore only ~1/6 of the row,
   *  so the images are requested small and a srcset lets the browser pick.
   *  IMAGE_HEIGHT keeps the intrinsic ratio hint aligned with the 143.8%
   *  aspect box in mdv-recs.css (300 * 1.438 = 431). */
  var IMAGE_WIDTH = 300;
  var IMAGE_HEIGHT = 431;
  var IMAGE_SRCSET_WIDTHS = [200, 300, 400, 600];
  var IMAGE_SIZES = '(min-width: 1024px) 16vw, (min-width: 768px) 24vw, 32vw';

  /* ---- cart drawer surface ------------------------------------------- *
   *  The drawer row is 3-up at 33.33% of a narrow panel, so tiles are
   *  IMAGE ONLY: at that width the legacy title rendered at 6px
   *  (cart-drawer.css .selected-content .selected-title h6) and was
   *  illegible. The product title survives as img alt + link aria-label,
   *  and every filter still runs - the 31% guard still decides WHICH
   *  products appear, it simply is not displayed here. */
  /*  6 tiles: the drawer's .selected-row is a wrapping flex grid at 33.33%
   *  per item, so six render as two rows of three (Reece, 2026-08-22). The
   *  merged multi-seed Worker list supplies well over six candidates, and the
   *  best-sellers backfill tops up to this cap exactly as it did to three. */
  var CART_MAX_TILES = 6;
  /* Terminal fallback is "hide the row cleanly", so the row is hidden only
     when nothing at all survives; 1-3 survivors still render. */
  var CART_MIN_TILES = 1;
  var CART_IMAGE_SIZES = '(max-width: 767px) 30vw, 150px';

  /*  Drawer image width. Shared by cartTileHtml and the precompute warmer:
   *  the warmed URL MUST be byte-identical to the rendered src or the warm
   *  is wasted and the swap still shows a blank tile. */
  var CART_IMAGE_WIDTH = 400;

  var CART_DEBOUNCE_MS = 300;

  /*  Paint scheduling budget. Data work starts eagerly; only the DOM write is
   *  deferred, and never by more than this. */
  var PAINT_TIMEOUT_MS = 300;

  /*  Seed weighting for the merged cart list. The contract fixes the shape
   *  - "summed lift, last-added weighted highest" - but not the multiplier;
   *  2x/1x is the minimal reading of "weighted highest". Exposed as a
   *  constant so it can be retuned without touching the merge logic. */
  var SEED_WEIGHT_LAST_ADDED = 2;
  var SEED_WEIGHT_OTHER = 1;
  var BEST_SELLERS_LIMIT = 24;

  /* ------------------------------------------------------------------ *
   * SKU parsing - ported from mdv_recs/skus.py (the tested reference).
   * ------------------------------------------------------------------ */

  /* Style code: alpha prefix + digits, optionally a ".N" version suffix. */
  var STYLE_RE = /^([A-Za-z]+[0-9]+)(?:\.([0-9]+))?$/;

  /*  SKU shape is STYLE-COLOUR-SIZE.  The SIZE segment may itself contain a
   *  hyphen (ACC1387-01-S-M, ACC1375-11-XXS-XS), so parsing MUST split from
   *  the LEFT with maxsplit=2.  Splitting on the last hyphen corrupts those.
   *  The ".N" suffix marks a newer version of the same style and is stripped
   *  to form the style root (WMN3870.2-06-12 -> WMN3870).
   */
  function parseSku(sku) {
    if (!sku) { return null; }
    var s = String(sku).trim().toUpperCase();
    if (!s) { return null; }

    var first = s.indexOf('-');
    if (first === -1) { return null; }
    var second = s.indexOf('-', first + 1);
    if (second === -1) { return null; }

    var styleRaw = s.slice(0, first);
    var colour = s.slice(first + 1, second);
    var size = s.slice(second + 1);
    if (!colour || !size) { return null; }

    var m = STYLE_RE.exec(styleRaw);
    if (!m) { return null; }

    return {
      raw: s,
      styleRoot: m[1],
      version: m[2] ? parseInt(m[2], 10) : null,
      colour: colour,
      size: size
    };
  }

  function styleRootOf(sku) {
    var parsed = parseSku(sku);
    return parsed ? parsed.styleRoot : null;
  }

  function colourwayRootOf(sku) {
    var parsed = parseSku(sku);
    return parsed ? parsed.styleRoot + '-' + parsed.colour : null;
  }

  /* ------------------------------------------------------------------ *
   * Colour join key - ported from mdv_recs/colours.py.
   *
   * The key must be computed IDENTICALLY here and in the pipeline, so this
   * is a line-for-line port, verified row-for-row against the Python
   * implementation's own output (see _tests/py-colour-reference.py).
   *
   * The measured decision (docs/colour-join-measurement.md) was the
   * normalised trailing title colour phrase, NOT the SKU colour code: only
   * 5.4% of displayed rows had colour codes pure enough, because codes are
   * merchandising slots reused across fabrics (code 04 spans olive, sage and
   * khaki).
   *
   * Normalisation folds PRESENTATION ONLY - case, punctuation, multi-colour
   * word order, gray/grey spelling.  It must NEVER merge distinct shades:
   * sage and olive stay separate, because merging them re-introduces exactly
   * the substitution error this work exists to remove.
   * ------------------------------------------------------------------ */

  /* Word-order and spelling folds only. NOT a shade-merging table. */
  var COLOUR_SYNONYMS = {
    'marl grey': 'grey marl',
    'gray': 'grey',
    'gray marl': 'grey marl',
    'marl gray': 'grey marl',
    'charcoal': 'charcoal grey',
    'wine': 'wine red',
    'offwhite': 'off white',
    'off-white': 'off white'
  };

  var COLOUR_PUNCT = /[^a-z0-9/ ]+/g;
  var COLOUR_WS = /\s+/g;

  /* Mirrors Python's dict.get(p, p) without inheriting Object.prototype
     members ('constructor' etc. must fold to themselves, not to a function). */
  function colourSynonym(word) {
    return Object.prototype.hasOwnProperty.call(COLOUR_SYNONYMS, word)
      ? COLOUR_SYNONYMS[word]
      : word;
  }

  /* Byte-for-byte equivalent of colours.normalise_colour_word. */
  function normaliseColourWord(raw) {
    if (!raw) { return null; }
    var s = String(raw).trim().toLowerCase();
    s = s.replace(COLOUR_PUNCT, ' ');
    s = s.replace(COLOUR_WS, ' ').trim();
    if (!s) { return null; }

    if (s.indexOf('/') !== -1) {
      /* Multi-colour phrases: the order is presentational, so canonicalise
         it. Synonyms apply per part, BEFORE sorting. */
      var split = s.split('/');
      var kept = [];
      for (var i = 0; i < split.length; i++) {
        var part = split[i].replace(COLOUR_WS, ' ').trim();
        if (part) { kept.push(colourSynonym(part)); }
      }
      if (!kept.length) { return null; }
      kept.sort(function (a, b) { return a < b ? -1 : (a > b ? 1 : 0); });
      return kept.join('/');
    }

    return colourSynonym(s);
  }

  /*  THE JOIN KEY. Equivalent of colours.colour_key_from_title.
   *  MDV titles are '<Name> - <Description> - <Colour>'.  Returns null when
   *  there is no ' - ' separator, and the caller MUST treat a null key as
   *  "no colour match" - never as a wildcard. */
  function colourKeyFromTitle(title) {
    if (!title) { return null; }
    var s = String(title);
    var at = s.lastIndexOf(' - ');
    if (at === -1) { return null; }
    return normaliseColourWord(s.slice(at + 3));
  }

  /* ------------------------------------------------------------------ *
   * Store identity
   * ------------------------------------------------------------------ */

  /*  Worker candidates carry a handle per storefront.  The myshopify handles
   *  are historical and two of them are traps:
   *    maniere-de-voir-au  = MIDDLE EAST  (NOT Australia)
   *    manire-de-voir-aus  = AUSTRALIA    (misspelling is canonical)
   */
  var SHOP_TO_STORE_KEY = {
    'manieredevoiruk': 'uk',
    'maniere-de-voir-us': 'us',
    'maniere-de-voir-eu': 'eu',
    'maniere-de-voir-ca': 'ca',
    'maniere-de-voir-au': 'me',
    'manire-de-voir-aus': 'au'
  };

  /*  Returns the per-store handle key, or null when the shop cannot be
   *  identified.  A null key deliberately skips the Worker tier altogether
   *  and drops straight to the store-local best-sellers ladder, rather than
   *  guessing a key and rendering another store's product handles (404s).
   */
  function storeKey() {
    var shop = '';
    try {
      if (typeof Shopify !== 'undefined' && Shopify && Shopify.shop) {
        shop = String(Shopify.shop);
      }
    } catch (e) { shop = ''; }
    if (shop) {
      var handle = shop.replace(/\.myshopify\.com$/i, '').toLowerCase();
      if (Object.prototype.hasOwnProperty.call(SHOP_TO_STORE_KEY, handle)) {
        return SHOP_TO_STORE_KEY[handle];
      }
    }
    return null;
  }

  /* ------------------------------------------------------------------ *
   * Networking - every helper resolves, never rejects.
   * ------------------------------------------------------------------ */

  function fetchJson(url, timeoutMs, options) {
    var opts = options || {};
    return new Promise(function (resolve) {
      if (typeof fetch !== 'function') { resolve(null); return; }

      var settled = false;
      var controller = null;
      try {
        if (typeof AbortController === 'function') { controller = new AbortController(); }
      } catch (e) { controller = null; }

      var timer = setTimeout(function () {
        if (settled) { return; }
        settled = true;
        if (controller) { try { controller.abort(); } catch (e) {} }
        resolve(null);
      }, timeoutMs);

      var init = { credentials: opts.credentials || 'same-origin' };
      if (controller) { init.signal = controller.signal; }

      var finish = function (value) {
        if (settled) { return; }
        settled = true;
        clearTimeout(timer);
        resolve(value);
      };

      try {
        fetch(url, init).then(function (res) {
          if (!res || !res.ok) { return null; }
          return res.json();
        }).then(function (data) {
          finish(data || null);
        })['catch'](function () {
          finish(null);
        });
      } catch (e) {
        finish(null);
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * v3 display ladder
   * ------------------------------------------------------------------ */

  /* Own-property read that treats absent, null and inherited alike as
     "missing", so a colour key such as 'constructor' cannot resolve. */
  function ownProp(obj, key) {
    if (!obj || typeof obj !== 'object') { return null; }
    if (key === null || key === undefined || key === '') { return null; }
    if (!Object.prototype.hasOwnProperty.call(obj, key)) { return null; }
    var value = obj[key];
    return (value === null || value === undefined) ? null : value;
  }

  /*  Resolves ONE candidate to the single colourway that will be displayed.
   *
   *    tier 1 'observed'    by_anchor_colour[anchorKey] names a candidate
   *                         colourway -> colourways[thatKey][storeKey]
   *    tier 2 'same_colour' colourways[anchorKey][storeKey]
   *    tier 3 'default'     default[storeKey]
   *
   *  A tier that yields no live handle for this storefront falls through to
   *  the NEXT TIER.  There is deliberately no walk-down inside tier 1: the
   *  pipeline emits at most one observed colourway per anchor colour and the
   *  entry's presence is itself the evidence claim, so trying a different
   *  colourway here would invent a pairing the data does not support.
   *
   *  The returned colourway is final.  Callers must filter on it and, if it
   *  fails, drop the candidate - never swap to another colourway.
   */
  function resolveCandidateHandle(candidate, anchorKey, key) {
    if (!candidate || !key) { return null; }

    if (anchorKey) {
      /* tier 1 */
      var mappedKey = ownProp(candidate.by_anchor_colour, anchorKey);
      if (mappedKey) {
        var observed = ownProp(ownProp(candidate.colourways, mappedKey), key);
        if (observed) {
          return { handle: String(observed), colourKey: String(mappedKey), colourTier: 'observed' };
        }
        /* no walk-down: fall through to tier 2 */
      }

      /* tier 2 */
      var same = ownProp(ownProp(candidate.colourways, anchorKey), key);
      if (same) {
        return { handle: String(same), colourKey: String(anchorKey), colourTier: 'same_colour' };
      }
    }

    /* tier 3 */
    var fallback = ownProp(candidate['default'], key);
    if (fallback) {
      return { handle: String(fallback), colourKey: null, colourTier: 'default' };
    }

    /*  v2 compatibility: the flat `handles` map equals `default` in v3 and is
     *  the only map a v2 artifact carries.  Without this, an artifact
     *  rollback would resolve every candidate to null and silently push the
     *  whole PDP onto the best-sellers ladder. */
    var legacy = ownProp(candidate.handles, key);
    if (legacy) {
      return { handle: String(legacy), colourKey: null, colourTier: 'default' };
    }

    return null;
  }

  /* ------------------------------------------------------------------ *
   * Single-flight Worker cache
   *
   *  Keyed on style root, caching the IN-FLIGHT PROMISE rather than the
   *  result, so the PDP row, the predictive precompute and a cart refresh
   *  asking for the same root collapse into ONE request instead of three.
   *  Page-lifetime and unbounded on purpose: a page only ever touches a
   *  handful of roots (cart size + 1), and the Worker response is immutable
   *  for the life of a pageview.
   * ------------------------------------------------------------------ */
  var recsPromiseCache = {};

  function getRecsCached(rootValue) {
    if (!rootValue) { return Promise.resolve([]); }
    var key = String(rootValue);
    if (Object.prototype.hasOwnProperty.call(recsPromiseCache, key)) {
      return recsPromiseCache[key];
    }
    var pending = getRecs(key);
    recsPromiseCache[key] = pending;
    return pending;
  }

  function getRecs(rootValue) {
    if (!rootValue) { return Promise.resolve([]); }
    return fetchJson(
      RECS_ENDPOINT + encodeURIComponent(rootValue),
      FETCH_TIMEOUT_MS,
      { credentials: 'omit' }
    ).then(function (data) {
      if (!data || !Array.isArray(data.candidates)) { return []; }
      return data.candidates.filter(function (c) {
        return c && typeof c === 'object';
      });
    })['catch'](function () { return []; });
  }

  /*  Cart exclusion operates on the STYLE root: every colour and every
   *  version of a style already in the basket is suppressed. */
  function getCartRoots() {
    return fetchJson('/cart.js', FETCH_TIMEOUT_MS).then(function (cart) {
      var roots = {};
      if (!cart || !Array.isArray(cart.items)) { return roots; }
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        var root = styleRootOf(item && item.sku);
        if (root) { roots[root] = true; }
      }
      return roots;
    })['catch'](function () { return {}; });
  }

  /*  Reads the cart once and derives everything the cart surface needs.
   *
   *  "Last added" is the LAST parseable line in cart.items. Shopify appends
   *  new lines, so this is the freshest intent in the common case. Known
   *  limitation: re-adding an existing variant bumps that line's quantity in
   *  place rather than moving it to the end, so the anchor can lag in that
   *  one case. cart.js exposes no per-line timestamp, so this is the best
   *  available signal.
   */
  function readCartState() {
    return fetchJson('/cart.js', FETCH_TIMEOUT_MS).then(function (cart) {
      var state = {
        roots: {},          /* style roots present in the cart */
        seeds: [],          /* unique roots, cart order */
        lastAdded: null,    /* { root, title, vendor } */
        signature: '',      /* changes only when the cart really changes */
        itemCount: 0
      };
      if (!cart || !Array.isArray(cart.items)) { return state; }

      var parts = [];
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i] || {};
        var root = styleRootOf(item.sku);
        parts.push((root || item.sku || item.id || '?') + 'x' + (item.quantity || 0));
        if (!root) { continue; }
        state.itemCount++;
        if (!state.roots[root]) {
          state.roots[root] = true;
          state.seeds.push(root);
        }
        state.lastAdded = {
          root: root,
          title: String(item.product_title || item.title || ''),
          vendor: String(item.vendor || '')
        };
      }
      state.signature = parts.join('|');
      return state;
    })['catch'](function () {
      return { roots: {}, seeds: [], lastAdded: null, signature: '', itemCount: 0 };
    });
  }

  /*  Merges one Worker candidate list per cart seed into a single ranked
   *  list: score = SUM over seeds of (lift x seed weight), with the
   *  last-added seed weighted highest. Deduped by candidate root, keeping
   *  the richest candidate object seen for that root (so colourways from
   *  any seed's copy remain available to the ladder).
   *
   *  Ties keep the order in which the roots were first encountered, which
   *  is cart order with the last-added list processed first - deterministic,
   *  never alphabetical.
   */
  function mergeCandidateLists(entries) {
    var byRoot = {};
    var order = [];

    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (!entry || !Array.isArray(entry.candidates)) { continue; }
      var weight = entry.weight || SEED_WEIGHT_OTHER;

      for (var j = 0; j < entry.candidates.length; j++) {
        var candidate = entry.candidates[j];
        if (!candidate || typeof candidate !== 'object') { continue; }
        var root = candidate.root ? String(candidate.root) : '';
        if (!root) { continue; }

        var lift = (typeof candidate.lift === 'number' && isFinite(candidate.lift)) ? candidate.lift : 0;

        if (!Object.prototype.hasOwnProperty.call(byRoot, root)) {
          byRoot[root] = { candidate: candidate, score: 0, seen: 0 };
          order.push(root);
        }
        byRoot[root].score += lift * weight;
        byRoot[root].seen++;
        /* Prefer the copy carrying colour data, so the ladder has the most
           to work with regardless of which seed supplied it. */
        if (!byRoot[root].candidate.colourways && candidate.colourways) {
          byRoot[root].candidate = candidate;
        }
      }
    }

    var ranked = [];
    for (var k = 0; k < order.length; k++) {
      ranked.push(byRoot[order[k]]);
    }
    /* Stable: equal scores keep first-seen order. */
    ranked.sort(function (a, b) { return b.score - a.score; });

    var out = [];
    for (var n = 0; n < ranked.length; n++) { out.push(ranked[n].candidate); }
    return out;
  }

  /*  Fetches one Worker list per seed root and merges them. */
  function getMergedRecs(state, key) {
    if (!key || !state || !state.seeds.length) { return Promise.resolve([]); }

    var lastRoot = state.lastAdded ? state.lastAdded.root : null;
    var seeds = state.seeds.slice();
    /* Process the last-added seed first so ties resolve toward it. */
    seeds.sort(function (a, b) {
      if (a === lastRoot) { return -1; }
      if (b === lastRoot) { return 1; }
      return 0;
    });

    return mapLimit(seeds, CONCURRENCY, function (root) {
      return getRecsCached(root).then(function (candidates) {
        return {
          candidates: candidates,
          weight: (root === lastRoot) ? SEED_WEIGHT_LAST_ADDED : SEED_WEIGHT_OTHER
        };
      });
    }).then(function (entries) {
      return mergeCandidateLists(entries.filter(Boolean));
    })['catch'](function () { return []; });
  }

  function fetchProduct(handle) {
    if (!handle) { return Promise.resolve(null); }
    return fetchJson('/products/' + encodeURIComponent(handle) + '.js', FETCH_TIMEOUT_MS)
      .then(function (raw) { return normalizeProduct(raw); })
      ['catch'](function () { return null; });
  }

  function fetchBestSellers(collectionHandles) {
    return Promise.all(collectionHandles.map(function (handle) {
      return fetchJson(
        '/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + BEST_SELLERS_LIMIT,
        FETCH_TIMEOUT_MS
      ).then(function (data) {
        return (data && Array.isArray(data.products)) ? data.products : [];
      })['catch'](function () { return []; });
    })).then(function (lists) {
      var out = [];
      for (var i = 0; i < lists.length; i++) {
        for (var j = 0; j < lists[i].length; j++) { out.push(lists[i][j]); }
      }
      return out;
    })['catch'](function () { return []; });
  }

  /*  Bounded-concurrency map.  Resolves with one slot per input; a worker
   *  that throws or rejects yields null in its slot rather than failing the
   *  whole batch. */
  function mapLimit(items, limit, worker) {
    return new Promise(function (resolve) {
      var results = new Array(items.length);
      var index = 0;
      var active = 0;
      var finished = 0;

      if (!items.length) { resolve(results); return; }

      function runOne(i) {
        Promise.resolve().then(function () {
          return worker(items[i], i);
        }).then(function (value) {
          results[i] = value;
        }, function () {
          results[i] = null;
        }).then(function () {
          active--;
          finished++;
          if (finished === items.length) { resolve(results); }
          else { pump(); }
        });
      }

      function pump() {
        while (active < limit && index < items.length) {
          var i = index;
          index++;
          active++;
          runOne(i);
        }
      }

      pump();
    });
  }

  /* ------------------------------------------------------------------ *
   * Product normalisation
   * ------------------------------------------------------------------ */

  /*  The two product sources disagree on price units:
   *    /products/{handle}.js            -> number, already in cents (4500)
   *    /collections/{h}/products.json   -> decimal string ("45.00")
   *  typeof discriminates them safely. */
  function toCents(value) {
    if (value === null || value === undefined || value === '') { return null; }
    if (typeof value === 'number') {
      return isFinite(value) ? Math.round(value) : null;
    }
    var parsed = parseFloat(String(value));
    if (!isFinite(parsed)) { return null; }
    return Math.round(parsed * 100);
  }

  function imageSrcOf(entry) {
    if (!entry) { return ''; }
    if (typeof entry === 'string') { return entry; }
    if (entry.src) { return String(entry.src); }
    return '';
  }

  function normalizeProduct(raw) {
    if (!raw || typeof raw !== 'object') { return null; }

    var rawVariants = Array.isArray(raw.variants) ? raw.variants : [];
    var variants = [];
    for (var i = 0; i < rawVariants.length; i++) {
      var v = rawVariants[i] || {};
      variants.push({
        sku: v.sku ? String(v.sku) : '',
        available: !!v.available,
        price: toCents(v.price),
        compareAt: toCents(v.compare_at_price)
      });
    }

    var image = '';
    if (Array.isArray(raw.images)) {
      for (var j = 0; j < raw.images.length && !image; j++) {
        image = imageSrcOf(raw.images[j]);
      }
    }
    if (!image) { image = imageSrcOf(raw.featured_image); }

    var handle = raw.handle ? String(raw.handle) : '';

    var product = {
      handle: handle,
      title: raw.title ? String(raw.title) : '',
      vendor: raw.vendor ? String(raw.vendor) : '',
      url: raw.url ? String(raw.url) : (handle ? '/products/' + handle : ''),
      image: image,
      variants: variants,
      available: false,
      styleRoot: null
    };

    for (var k = 0; k < variants.length; k++) {
      if (variants[k].available) { product.available = true; break; }
    }
    for (var n = 0; n < variants.length && !product.styleRoot; n++) {
      product.styleRoot = styleRootOf(variants[n].sku);
    }

    return product;
  }

  function firstAvailableVariant(product) {
    if (!product || !Array.isArray(product.variants)) { return null; }
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) { return product.variants[i]; }
    }
    return null;
  }

  /*  Discount on the candidate's FIRST AVAILABLE variant's own price pair.
   *  No rounding anywhere.  Returns null when the pair is unusable. */
  function markdownRatio(product) {
    var variant = firstAvailableVariant(product);
    if (!variant) { return null; }
    var price = variant.price;
    var compareAt = variant.compareAt;
    if (price === null) { return null; }
    if (compareAt === null || compareAt <= 0 || compareAt <= price) { return 0; }
    return (compareAt - price) / compareAt;
  }

  function passesMarkdownCap(product) {
    var ratio = markdownRatio(product);
    if (ratio === null) { return false; } /* unusable price data - exclude */
    return !(ratio > MAX_MARKDOWN);
  }

  /* ------------------------------------------------------------------ *
   * Runtime filter chain, applied in contract order.
   * ------------------------------------------------------------------ */
  function buildFilter(context) {
    var ctx = context || {};
    var cartRoots = ctx.cartRoots || {};
    return function (product) {
      if (!product || !product.handle) { return false; }

      /* (a) the current product's own style root (defensive) */
      if (product.styleRoot && ctx.ownRoot && product.styleRoot === ctx.ownRoot) { return false; }
      if (ctx.ownHandle && product.handle === ctx.ownHandle) { return false; }

      /* (b) anything already in the cart, by style root */
      if (product.styleRoot && cartRoots[product.styleRoot]) { return false; }

      /* (c) image present */
      if (!product.image) { return false; }

      /* (d) available */
      if (!product.available) { return false; }

      /* (e) universal 31% markdown cap */
      if (!passesMarkdownCap(product)) { return false; }

      return true;
    };
  }

  /*  Fallback gender routing, ported verbatim from the legacy template
   *  (templates/product.recommendation-product.liquid).  Liquid `contains`
   *  is case-sensitive and the Woman branch is evaluated first, so a vendor
   *  such as "Woman Tops" never reaches the Man branch even though the
   *  literal substring "man" occurs inside "Woman".  A vendor matching
   *  neither token falls through to BOTH collections, exactly as the legacy
   *  template did. */
  function bestSellerCollections(vendor) {
    var v = String(vendor || '');
    if (v.indexOf('Woman') !== -1 || v.indexOf('woman') !== -1) {
      return ['womens-best-sellers'];
    }
    if (v.indexOf('Man') !== -1 || v.indexOf('man') !== -1) {
      return ['mens-best-sellers'];
    }
    return ['womens-best-sellers', 'mens-best-sellers'];
  }

  /* ------------------------------------------------------------------ *
   * Tile assembly
   * ------------------------------------------------------------------ */

  /*  `colour` carries the v3 ladder outcome for the bought-with tier.  The
   *  best-sellers tier passes nothing: those are products, not roots, and
   *  have no colour ladder. */
  function toTile(product, tier, colour) {
    var variant = firstAvailableVariant(product);
    return {
      handle: product.handle,
      title: product.title,
      url: product.url,
      image: product.image,
      styleRoot: product.styleRoot || null,
      tier: tier,
      colourKey: (colour && colour.colourKey) ? colour.colourKey : null,
      colourTier: (colour && colour.colourTier) ? colour.colourTier : null,
      price: variant ? variant.price : null,
      compareAt: variant ? variant.compareAt : null
    };
  }

  function dedupeKey(product) {
    return product.styleRoot || product.handle;
  }

  /*  Runs the full ladder and returns the tiles to render (possibly fewer
   *  than MIN_TILES, in which case the caller hides the section). */
  function buildTiles(ctx, options) {
    var opts = options || {};
    var maxTiles = opts.maxTiles || MAX_TILES;
    var minTiles = opts.minTiles || MIN_TILES;

    return getCartRoots().then(function (cartRoots) {
      var filter = buildFilter({
        ownRoot: ctx.ownRoot,
        ownHandle: ctx.ownHandle,
        cartRoots: cartRoots
      });

      var key = ctx.storeKey;
      /*  PDP seeds from the single product on the page; the cart surface
       *  supplies its own merged multi-seed list via opts.candidates. */
      var candidatesPromise = opts.candidates
        ? opts.candidates()
        : getRecsCached(key ? ctx.ownRoot : null);

      return candidatesPromise.then(function (candidates) {
        var wanted = [];
        for (var i = 0; i < candidates.length; i++) {
          var candidate = candidates[i];
          /* The display ladder picks ONE colourway per candidate. */
          var resolved = resolveCandidateHandle(candidate, ctx.anchorColourKey, key);
          if (!resolved) { continue; } /* no live handle on this storefront - skip */
          wanted.push({
            handle: resolved.handle,
            root: candidate.root || null,
            colourKey: resolved.colourKey,
            colourTier: resolved.colourTier
          });
        }

        return mapLimit(wanted, CONCURRENCY, function (entry) {
          return fetchProduct(entry.handle).then(function (product) {
            /* Trust the Worker's root when the storefront payload has no
               parseable SKU, so cart/own-root exclusion still applies. */
            if (product && !product.styleRoot && entry.root) {
              product.styleRoot = String(entry.root).toUpperCase();
            }
            if (product) {
              product.colourKey = entry.colourKey;
              product.colourTier = entry.colourTier;
            }
            return product;
          });
        }).then(function (products) {
          var picked = [];
          var seen = {};
          for (var n = 0; n < products.length && picked.length < maxTiles; n++) {
            var product = products[n];
            /*  POST-CHOICE FILTERING: image / availability / 31% guard are
             *  evaluated on the CHOSEN colourway's product.  A chosen
             *  colourway that violates them EXCLUDES the whole candidate -
             *  we never swap to a different colourway to rescue it. */
            if (!product || !filter(product)) { continue; }
            var k = dedupeKey(product);
            if (seen[k]) { continue; }
            seen[k] = true;
            picked.push(toTile(product, 'bought_with', product));
          }
          /*  PDP backfills only when the bought-with tier came up short of
           *  MIN_TILES. The cart row is a fixed 3-up grid, so it always
           *  tops up to a full row (opts.alwaysFill). */
          var needsFill = opts.alwaysFill
            ? (picked.length < maxTiles)
            : (picked.length < minTiles);
          if (!needsFill) { return picked; }
          return fillFromBestSellers(picked, seen, filter, ctx, maxTiles);
        });
      });
    })['catch'](function () { return []; });
  }

  function fillFromBestSellers(picked, seen, filter, ctx, maxTiles) {
    var cap = maxTiles || MAX_TILES;
    return fetchBestSellers(bestSellerCollections(ctx.ownVendor)).then(function (raws) {
      for (var i = 0; i < raws.length && picked.length < cap; i++) {
        var product = normalizeProduct(raws[i]);
        if (!product || !filter(product)) { continue; }
        var k = dedupeKey(product);
        if (seen[k]) { continue; }
        seen[k] = true;
        picked.push(toTile(product, 'best_sellers'));
      }
      return picked;
    })['catch'](function () { return picked; });
  }

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */

  function esc(value) {
    return String(value === null || value === undefined ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /*  Idempotent: any width already on the URL is replaced rather than kept,
   *  so building a srcset from an already-sized source still yields distinct
   *  entries instead of N copies of the same URL. */
  function sizedImage(src, width) {
    if (!src) { return ''; }
    var url = String(src);
    if (url.indexOf('//') === 0) { url = 'https:' + url; }

    var hash = '';
    var hashAt = url.indexOf('#');
    if (hashAt !== -1) { hash = url.slice(hashAt); url = url.slice(0, hashAt); }

    var queryAt = url.indexOf('?');
    var base = queryAt === -1 ? url : url.slice(0, queryAt);
    var query = queryAt === -1 ? '' : url.slice(queryAt + 1);

    var kept = [];
    if (query) {
      var pairs = query.split('&');
      for (var i = 0; i < pairs.length; i++) {
        if (pairs[i] && pairs[i].indexOf('width=') !== 0) { kept.push(pairs[i]); }
      }
    }
    kept.push('width=' + width);
    return base + '?' + kept.join('&') + hash;
  }

  function imageSrcset(src) {
    if (!src) { return ''; }
    var entries = [];
    for (var i = 0; i < IMAGE_SRCSET_WIDTHS.length; i++) {
      var w = IMAGE_SRCSET_WIDTHS[i];
      entries.push(sizedImage(src, w) + ' ' + w + 'w');
    }
    return entries.join(', ');
  }

  /*  Uses the theme's own money formatting.  data-shop-moneyformat is set on
   *  <body> in layout/theme.liquid and already carries the per-store currency
   *  suffix conventions (e.g. the AED suffix rewrite). */
  function moneyFormat() {
    var fmt = '';
    try {
      if (typeof document !== 'undefined' && document.body && document.body.getAttribute) {
        fmt = document.body.getAttribute('data-shop-moneyformat') || '';
      }
    } catch (e) { fmt = ''; }
    if (!fmt) {
      try {
        if (typeof Shopify !== 'undefined' && Shopify && Shopify.money_format) {
          fmt = String(Shopify.money_format);
        }
      } catch (e) { fmt = ''; }
    }
    return fmt;
  }

  function money(cents) {
    if (cents === null || cents === undefined) { return ''; }
    var fmt = moneyFormat();
    try {
      if (fmt && typeof Shopify !== 'undefined' && Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, fmt);
      }
    } catch (e) { /* fall through */ }
    return (cents / 100).toFixed(2);
  }

  function tileHtml(tile, position) {
    var onSale = (tile.compareAt !== null && tile.compareAt !== undefined &&
                  tile.price !== null && tile.compareAt > tile.price);

    /*  The sale red is NOT duplicated here.  These are the theme's own price
     *  classes, and the theme's own rule paints them:
     *    assets/theme.css.liquid          .product_price_wrap .actual-price1.onsale
     *    snippets/global-styles.liquid    body.template-product .prices ... .onsale
     *  both resolve to var(--sale-red), defined in snippets/font-preconnect.liquid.
     *  The struck compare-at keeps the theme's neutral .compare-price styling,
     *  exactly as global-styles.liquid says it should. */
    /*  money() output is NOT esc()'d: the store money format is shop-controlled
     *  and may legitimately contain markup (ME: "<span class=money>{{amount_no_decimals}} AED</span>").
     *  The theme's own code injects Shopify.formatMoney output unescaped for the
     *  same reason (custom.js cart rendering). The numeric amount is generated,
     *  never user input. */
    var priceHtml = '<span class="mdv-recs-price mdv-tile-price actual-price1 money' +
      (onSale ? ' onsale' : '') + '">' + money(tile.price) + '</span>';
    if (onSale) {
      priceHtml += '<s class="mdv-recs-compare mdv-tile-compare compare-price">' +
        money(tile.compareAt) + '</s>';
    }

    return '<div class="swiper-slide mdv-recs-slide"' +
      ' data-mdv-recs-tier="' + esc(tile.tier) + '"' +
      ' data-mdv-recs-root="' + esc(tile.styleRoot || '') + '"' +
      ' data-mdv-recs-position="' + position + '">' +
      '<a class="mdv-recs-link" href="' + esc(tile.url) + '">' +
        '<span class="mdv-recs-image">' +
          '<img src="' + esc(sizedImage(tile.image, IMAGE_WIDTH)) + '"' +
            ' srcset="' + esc(imageSrcset(tile.image)) + '"' +
            ' sizes="' + esc(IMAGE_SIZES) + '"' +
            ' alt="' + esc(tile.title) + '" loading="lazy"' +
            ' width="' + IMAGE_WIDTH + '" height="' + IMAGE_HEIGHT + '">' +
        '</span>' +
        '<span class="mdv-recs-meta mdv-tile-meta">' +
          '<span class="mdv-recs-name mdv-tile-name">' + esc(tile.title) + '</span>' +
          '<span class="mdv-recs-price-wrap mdv-tile-price-wrap product_price_wrap' +
            (onSale ? ' onsale' : '') + '">' + priceHtml + '</span>' +
        '</span>' +
      '</a>' +
      '</div>';
  }

  /*  Cart drawer tile: IMAGE ONLY.
   *
   *  No title or price markup is emitted - at 33.33% of a narrow drawer the
   *  legacy title rendered at 6px and was illegible. The title is preserved
   *  for accessibility as the img alt and the link aria-label, so screen
   *  readers and hover tooltips still identify the product.
   *
   *  Reuses the drawer's own .selected-item / .selected-image classes so the
   *  existing 3-up grid density is inherited rather than re-implemented. */
  function cartTileHtml(tile, position) {
    var label = tile.title || 'Product';
    return '<div class="selected-item mdv-recs-cart-item"' +
      ' data-mdv-recs-tier="' + esc(tile.tier) + '"' +
      ' data-mdv-recs-root="' + esc(tile.styleRoot || '') + '"' +
      ' data-mdv-recs-position="' + position + '">' +
      /*  Drawer images mirror the LEGACY markup exactly: plain <img>, no
       *  width/height attributes, no srcset/sizes (Reece, 2026-08-22: the
       *  hinted 143.8% aspect + small-image sizes distorted and blurred the
       *  tiles vs the previous design).  The drawer's own cart-drawer.css
       *  33.33% columns and the image's natural ratio govern, as before.
       *  Src requested at 400px: the ~125px column is retina-safe at 3x and
       *  the bytes halve vs 600.  loading="eager" is required, not optional:
       *  the drawer is hidden at load, and a lazy image in a display:none
       *  subtree never intersects, so it only started fetching at drawer-open
       *  and the user watched six tiles pop in (Reece, 22 Aug: "still
       *  laggy").  fetchpriority="low" keeps the six warm fetches from
       *  contending with LCP/critical resources site-wide. */
      '<div class="selected-image">' +
        '<a href="' + esc(tile.url) + '" aria-label="' + esc(label) + '">' +
          '<img src="' + esc(sizedImage(tile.image, CART_IMAGE_WIDTH)) + '"' +
            ' alt="' + esc(label) + '" loading="eager" fetchpriority="low">' +
        '</a>' +
      '</div>' +
      '</div>';
  }

  /*  Exactly one analytics event, on click.  There is deliberately no render
   *  or impression event.  GTM (dataLayer) is the theme's primary transport,
   *  so gtag is only used when no dataLayer exists - firing both would
   *  double-count into the same GA4 property. */
  function trackClick(payload) {
    try {
      if (typeof window === 'undefined') { return; }
      var detail = {
        tier: payload.tier,
        surface: payload.surface || 'pdp',
        root: payload.root,
        candidate_root: payload.candidate_root,
        position: payload.position
      };
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({
          event: 'mdv_recs_click',
          tier: detail.tier,
          surface: detail.surface,
          root: detail.root,
          candidate_root: detail.candidate_root,
          position: detail.position
        });
        return;
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'mdv_recs_click', detail);
      }
    } catch (e) { /* telemetry must never break navigation */ }
  }

  function attachTelemetry(el, ctx, surface) {
    if (!el || el.getAttribute('data-mdv-recs-telemetry') === 'on') { return; }
    el.setAttribute('data-mdv-recs-telemetry', 'on');
    /* One delegated listener, bound once, so re-rendering the cart row on a
       mutation cannot stack duplicate handlers or double-fire the event. */
    var tileClass = (surface === 'cart') ? 'mdv-recs-cart-item' : 'mdv-recs-slide';
    el.addEventListener('click', function (event) {
      var node = event.target;
      while (node && node !== el && !(node.className && String(node.className).indexOf(tileClass) !== -1)) {
        node = node.parentNode;
      }
      if (!node || node === el || !node.getAttribute) { return; }
      trackClick({
        tier: node.getAttribute('data-mdv-recs-tier') || '',
        surface: surface || 'pdp',
        root: ctx.ownRoot || '',
        candidate_root: node.getAttribute('data-mdv-recs-root') || '',
        position: parseInt(node.getAttribute('data-mdv-recs-position'), 10) || 0
      });
    });
  }

  function initSwiper(el) {
    var container = el.querySelector('.mdv-recs-swiper');
    if (!container) { return null; }
    if (typeof Swiper !== 'function') {
      el.className += ' mdv-recs--noswiper';
      return null;
    }
    try {
      return new Swiper(container, {
        /*  ONE horizontal row, always.  direction is pinned to 'horizontal'
         *  and the wrapper is flex-wrap:nowrap in CSS, so the tiles can
         *  never stack or wrap vertically at any viewport. */
        direction: 'horizontal',
        /*  Density matches the legacy module: 3 across on mobile, 4 on
         *  tablet, 6 on desktop.  Swiper breakpoints are min-width. */
        slidesPerView: 3,
        spaceBetween: 9,
        breakpoints: {
          768: { slidesPerView: 4, spaceBetween: 12 },
          1024: { slidesPerView: 6, spaceBetween: 12 }
        },
        freeMode: true,
        speed: 1200,
        /*  forceToAxis stops the carousel hijacking page scroll: a vertical
         *  wheel or trackpad gesture over the row scrolls the PAGE, and only
         *  horizontal gestures move the carousel. */
        mousewheel: { forceToAxis: true },
        /*  dragSize: fixed at ~30% of the track rather than Swiper's
         *  proportional default.  With 6 of 8 tiles visible the proportional
         *  drag fills 75% of the track and reads as "nothing more here" -
         *  the opposite of the discoverability the bar exists for (Reece,
         *  2026-08-22).  Recomputed on swiper resize.  Falls back to 'auto'
         *  where the container has no measurable width (tests, hidden). */
        scrollbar: {
          el: el.querySelector('.mdv-recs-scrollbar'),
          draggable: true,
          dragSize: (container.clientWidth > 0) ? Math.round(container.clientWidth * 0.3) : 'auto'
        },
        on: {
          resize: function (sw) {
            try {
              var w = sw.el && sw.el.clientWidth;
              if (w > 0 && sw.params && sw.params.scrollbar) {
                sw.params.scrollbar.dragSize = Math.round(w * 0.3);
                if (sw.scrollbar && sw.scrollbar.updateSize) { sw.scrollbar.updateSize(); }
              }
            } catch (e) { /* cosmetic only - never break the carousel */ }
          }
        }
      });
    } catch (e) {
      el.className += ' mdv-recs--noswiper';
      return null;
    }
  }

  function hide(el) {
    if (!el) { return; }
    el.setAttribute('data-mdv-recs-state', 'hidden');
    if (el.className && String(el.className).indexOf('mdv-recs--ready') !== -1) {
      el.className = String(el.className).replace(/\s*mdv-recs--ready/g, '');
    }
    try { el.style.display = 'none'; } catch (e) {}
  }

  function renderInto(el, tiles, ctx) {
    if (!el) { return 0; }
    var track = el.querySelector('.mdv-recs-track');
    if (!track || !tiles || tiles.length < MIN_TILES) {
      hide(el);
      return 0;
    }

    var html = '';
    for (var i = 0; i < tiles.length; i++) {
      html += tileHtml(tiles[i], i + 1);
    }
    track.innerHTML = html;

    el.setAttribute('data-mdv-recs-state', 'rendered');
    el.setAttribute('data-mdv-recs-tier', tiles[0].tier);
    if (String(el.className).indexOf('mdv-recs--ready') === -1) {
      el.className += ' mdv-recs--ready';
    }

    attachTelemetry(el, ctx, 'pdp');
    initSwiper(el);
    return tiles.length;
  }

  /*  Cart drawer render. Writes the drawer's own .selected-item markup into
   *  the existing .selected-row container, so the panel's 3-up grid, gutters
   *  and spacing all still come from cart-drawer.css.
   *
   *  Hiding is done on the .selected-for-you wrapper (title included), so a
   *  failed run leaves no orphaned "Selected for You" heading above an empty
   *  row. */
  function cartWrapperOf(el) {
    var node = el;
    while (node && node.className !== undefined) {
      if (String(node.className).indexOf('selected-for-you') !== -1) { return node; }
      node = node.parentNode;
    }
    return null;
  }

  /*  NO-CLEAR ATOMIC SWAP.
   *
   *  A rendered row is never blanked as part of a refresh. The new markup is
   *  assembled as one string and assigned in a SINGLE innerHTML write, so
   *  the old tiles are replaced in one frame with no empty intermediate
   *  state. The only path that empties the row is a genuine transition to
   *  "nothing to show", which hides the whole block rather than leaving an
   *  orphan heading.
   */
  function renderCartInto(el, tiles, ctx) {
    if (!el) { return 0; }
    var wrapper = cartWrapperOf(el);
    var hasRendered = el.getAttribute('data-mdv-recs-state') === 'rendered';

    if (!tiles || tiles.length < CART_MIN_TILES) {
      /*  Genuine empty state, not a transient. Clearing here is correct:
       *  leaving stale recommendations for a basket that no longer implies
       *  them is worse than hiding. */
      el.innerHTML = '';
      if (wrapper) { try { wrapper.style.display = 'none'; } catch (e) {} }
      el.setAttribute('data-mdv-recs-state', 'hidden');
      cartRenderedSeedKey = null;
      return 0;
    }
    /* hasRendered is read above purely so the single-write invariant below
       is explicit: there is no branch that empties a rendered row here. */
    if (hasRendered) { /* replaced in one assignment, never cleared first */ }

    var html = '';
    for (var i = 0; i < tiles.length; i++) {
      html += cartTileHtml(tiles[i], i + 1);
    }
    el.innerHTML = html;

    if (wrapper) { try { wrapper.style.display = ''; } catch (e) {} }
    el.setAttribute('data-mdv-recs-state', 'rendered');
    el.setAttribute('data-mdv-recs-tier', tiles[0].tier);
    attachTelemetry(el, ctx, 'cart');
    return tiles.length;
  }

  /* ------------------------------------------------------------------ *
   * Presenters
   * ------------------------------------------------------------------ */

  function readContext(el) {
    var ownRoot = null;
    var skus = (el.getAttribute('data-mdv-recs-skus') || '').split('|');
    for (var i = 0; i < skus.length && !ownRoot; i++) {
      ownRoot = styleRootOf(skus[i]);
    }
    var ownTitle = el.getAttribute('data-mdv-recs-title') || '';
    return {
      ownRoot: ownRoot,
      ownHandle: el.getAttribute('data-mdv-recs-handle') || '',
      ownVendor: el.getAttribute('data-mdv-recs-vendor') || '',
      ownTitle: ownTitle,
      /* null when the title has no ' - ' separator: treated as "no colour
         match", which sends every candidate to tier 3, never a wildcard. */
      anchorColourKey: colourKeyFromTitle(ownTitle),
      storeKey: storeKey()
    };
  }

  /*  Defers ONE DOM write, briefly.
   *
   *  requestIdleCallback with a short timeout rather than requestAnimationFrame:
   *  rAF never fires while a tab is backgrounded, which is what produces
   *  phantom multi-second "render times" in field measurements. The timeout
   *  guarantees the paint happens within PAINT_TIMEOUT_MS even on a hidden tab
   *  or a busy main thread. */
  function schedulePaint(fn) {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: PAINT_TIMEOUT_MS });
    } else {
      setTimeout(fn, 0);
    }
  }

  function mount(el, ctx) {
    if (!el) { return Promise.resolve(null); }
    var state = el.getAttribute('data-mdv-recs-state');
    if (state === 'running' || state === 'rendered' || state === 'hidden') {
      return Promise.resolve(null);
    }
    el.setAttribute('data-mdv-recs-state', 'running');

    /*  The network work below starts the moment mount() is called - it is
     *  deliberately NOT wrapped in an idle callback. Only the render is
     *  scheduled politely. The returned promise still resolves after paint,
     *  so callers and tests can await a fully rendered row. */
    return buildTiles(ctx).then(function (tiles) {
      return new Promise(function (resolve) {
        schedulePaint(function () {
          /*  The paint runs OUTSIDE the promise chain, so a throw here would
           *  escape uncaught, leave the promise unsettled and strand the
           *  element at data-mdv-recs-state="running" - an empty frame that
           *  never resolves or hides. Caught explicitly. */
          try {
            renderInto(el, tiles, ctx);
            resolve(tiles);
          } catch (e) {
            hide(el);
            resolve(null);
          }
        });
      });
    })['catch'](function () {
      hide(el);
      return null;
    });
  }

  /* PDP presenter - the only one wired up in this phase. */
  function mountPdp(target) {
    var el = target;
    if (!el && typeof document !== 'undefined') {
      el = document.querySelector('[data-mdv-recs="pdp"]');
    }
    if (!el) { return Promise.resolve(null); }
    return mount(el, readContext(el));
  }

  /* ------------------------------------------------------------------ *
   * Cart drawer presenter
   * ------------------------------------------------------------------ */

  var CART_SELECTOR = '.selected-row';
  var cartRunId = 0;          /* generation counter - kills stale renders */
  var cartLastSignature = null;
  var cartDebounceTimer = null;
  var cartWatching = false;
  var cartEverPopulated = false;  /* false until the first populate starts */
  var cartRenderedSeedKey = null; /* seed identity of what is on screen now */
  var cartPrecomputed = null;     /* { seedKey, tiles, ctx, ready } */

  /*  Image readiness budget for the atomic swap. decode() can reject on
   *  detached elements and has been seen to hang in Safari, so it is always
   *  RACED against this timeout - one slow image must never hold five ready
   *  tiles, nor hold a stale row on screen. */
  var IMAGE_READY_TIMEOUT_MS = 800;

  /*  SEED IDENTITY.
   *
   *  Generations are keyed on this, not on a bare counter: several
   *  recomputes can legitimately be in flight at once (add-click head start,
   *  ajaxComplete, reconcile), and the only safe question to ask when one
   *  lands is "does this describe the same basket the screen already
   *  shows?". Two runs with equal seedKey are interchangeable, so the later
   *  one is a no-op rather than a re-render. */
  function seedKeyOf(state, anchorColourKey) {
    if (!state) { return ''; }
    var roots = (state.seeds || []).slice().sort();
    return roots.join(',') +
      '|last=' + ((state.lastAdded && state.lastAdded.root) || '') +
      '|colour=' + (anchorColourKey || '');
  }

  /*  Resolves when every image is decoded, or when the timeout wins -
   *  whichever comes first. Individual promises always RESOLVE (never
   *  reject), so Promise.all is safe here and behaves like allSettled
   *  without depending on it. Stragglers simply pop in individually
   *  afterwards, which is fine: they land in tiles that are already on
   *  screen rather than behind a blank row. */
  function warmImages(urls, timeoutMs) {
    if (typeof Image !== 'function' || !urls || !urls.length) {
      return Promise.resolve();
    }
    var each = [];
    for (var i = 0; i < urls.length; i++) {
      each.push(warmOneImage(urls[i]));
    }
    return Promise.race([
      Promise.all(each),
      new Promise(function (resolve) { setTimeout(resolve, timeoutMs); })
    ]);
  }

  function warmOneImage(url) {
    return new Promise(function (resolve) {
      if (!url) { resolve(); return; }
      try {
        var img = new Image();
        img.src = url;
        if (typeof img.decode === 'function') {
          img.decode().then(function () { resolve(); }, function () { resolve(); });
        } else {
          img.onload = function () { resolve(); };
          img.onerror = function () { resolve(); };
        }
      } catch (e) {
        resolve();
      }
    });
  }

  function tileImageUrls(tiles) {
    var urls = [];
    for (var i = 0; i < (tiles || []).length; i++) {
      if (tiles[i] && tiles[i].image) {
        urls.push(sizedImage(tiles[i].image, CART_IMAGE_WIDTH));
      }
    }
    return urls;
  }

  function cartContainer(target) {
    if (target) { return target; }
    if (typeof document === 'undefined') { return null; }
    return document.querySelector('[data-mdv-recs="cart"]') ||
           document.querySelector(CART_SELECTOR);
  }

  /*  Builds the cart row once. Every call takes a generation id; if another
   *  run starts before this one resolves, the stale result is discarded
   *  rather than written, so overlapping cart mutations cannot race a wrong
   *  row into the drawer. */
  /*  The ONE cart context builder. Both the live cart run and the predictive
   *  precompute go through this and computeCartTiles below, so the predicted
   *  set cannot drift from the real one - same seed merge, same in-cart-root
   *  exclusion, same 31% cap, same colour ladder. */
  function ctxFromState(state) {
    var anchorTitle = (state && state.lastAdded) ? state.lastAdded.title : '';
    return {
      /* Seeded and anchored on the LAST-ADDED line: freshest intent, and
         consistent with the seed weighting. */
      ownRoot: (state && state.lastAdded) ? state.lastAdded.root : null,
      ownHandle: '',
      ownVendor: (state && state.lastAdded) ? state.lastAdded.vendor : '',
      ownTitle: anchorTitle,
      anchorColourKey: colourKeyFromTitle(anchorTitle),
      storeKey: storeKey()
    };
  }

  function computeCartTiles(state) {
    var ctx = ctxFromState(state);
    return buildTiles(ctx, {
      maxTiles: CART_MAX_TILES,
      minTiles: CART_MIN_TILES,
      alwaysFill: true,
      candidates: function () { return getMergedRecs(state, ctx.storeKey); }
    }).then(function (tiles) {
      return { tiles: tiles, ctx: ctx, seedKey: seedKeyOf(state, ctx.anchorColourKey) };
    });
  }

  function runCart(el, runId, options) {
    var opts = options || {};
    cartEverPopulated = true;
    var statePromise = opts.state
      ? Promise.resolve(opts.state)
      : readCartState();

    return statePromise.then(function (state) {
      if (runId !== cartRunId) { return null; }

      if (!state.itemCount || !state.lastAdded) {
        /* Empty cart (or no parseable SKUs): nothing to seed from. */
        if (runId === cartRunId) { renderCartInto(el, [], { ownRoot: null }); }
        return null;
      }

      /*  RECONCILE FAST PATH: if this run describes the same basket the
       *  screen already shows - which is the common case when the confirmed
       *  cart matches what the precompute predicted - there is nothing to
       *  do. No re-render, no flicker, no wasted work. */
      var predictedKey = seedKeyOf(state, ctxFromState(state).anchorColourKey);
      if (predictedKey && predictedKey === cartRenderedSeedKey) {
        cartLastSignature = state.signature;
        return null;
      }

      return computeCartTiles(state).then(function (built) {
        if (runId !== cartRunId) { return null; }
        /*  Warm the images BEFORE swapping, so the new row appears complete
         *  rather than as six blank frames filling in. Raced against
         *  IMAGE_READY_TIMEOUT_MS - stragglers pop in individually. */
        return warmImages(tileImageUrls(built.tiles), IMAGE_READY_TIMEOUT_MS)
          .then(function () {
            if (runId !== cartRunId) { return null; }
            cartLastSignature = state.signature;
            cartRenderedSeedKey = built.seedKey;
            renderCartInto(el, built.tiles, built.ctx);
            return built.tiles;
          });
      });
    })['catch'](function () {
      if (runId === cartRunId) { renderCartInto(el, [], { ownRoot: null }); }
      return null;
    });
  }

  /*  Public entry point. Safe to call repeatedly. */
  function mountCart(target, options) {
    var el = cartContainer(target);
    if (!el) { return Promise.resolve(null); }
    var opts = options || {};

    if (!opts.force && cartLastSignature !== null) {
      /* Skip a redundant rebuild when the cart has not actually changed. */
      return readCartState().then(function (state) {
        if (state.signature === cartLastSignature) { return null; }
        cartRunId++;
        return runCart(el, cartRunId);
      })['catch'](function () { return null; });
    }

    cartRunId++;
    return runCart(el, cartRunId);
  }

  /*  Precise cart-endpoint test.
   *
   *  A bare indexOf('/cart') also matches /collections/cartier,
   *  /products/cartoon-tee and anything else whose path segment merely starts
   *  with "cart", each of which would have triggered a spurious cart rebuild
   *  (found in the 2026-08-22 variant-latency audit). Query string and hash
   *  are stripped first, and an optional market/locale prefix such as /en-au
   *  is tolerated. */
  function isCartUrl(rawUrl) {
    if (!rawUrl) { return false; }
    var path = String(rawUrl).split('#')[0].split('?')[0];
    path = path.replace(/^https?:\/\/[^/]+/i, '');   /* absolute            */
    path = path.replace(/^\/\/[^/]+/, '');           /* protocol-relative   */
    if (path.charAt(0) !== '/') { path = '/' + path; }            /* relative */
    if (path.length > 1) { path = path.replace(/\/+$/, ''); }     /* trailing */
    path = path.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i, '');
    return /^\/cart(\.js|\.json)?$/i.test(path) ||
           /^\/cart\/(add|change|update|clear)(\.js|\.json)?$/i.test(path);
  }

  /* ------------------------------------------------------------------ *
   * Predictive precompute
   *
   *  The drawer AUTO-OPENS on add-to-cart, and an add changes the seed - the
   *  added item becomes last-added (weighted 2x) and its colour becomes the
   *  anchor. So the recompute that matters happens exactly at auto-open, and
   *  a load-time warm only ever helped manual opens.
   *
   *  This precomputes the POST-ADD state - current cart plus this product as
   *  last-added - and warms its images, so the add renders from memory with
   *  no blank row. It is held in `cartPrecomputed` and only painted when an
   *  add actually happens; a manual drawer-open still shows the true
   *  current-cart set.
   *
   *  COST, stated honestly: one Worker fetch per seed root, i.e. cart size
   *  plus one, minus anything the single-flight cache already has, plus ~6
   *  warm image fetches per PDP view. The image fetches are the same URLs the
   *  drawer would request anyway, so they are pulled forward rather than
   *  added; the Worker fetches are genuinely extra.
   * ------------------------------------------------------------------ */

  function predictedCartState(state, product) {
    var seeds = (state.seeds || []).slice();
    if (product.root && seeds.indexOf(product.root) === -1) { seeds.push(product.root); }
    var roots = {};
    for (var k in (state.roots || {})) {
      if (Object.prototype.hasOwnProperty.call(state.roots, k)) { roots[k] = true; }
    }
    if (product.root) { roots[product.root] = true; }
    return {
      roots: roots,
      seeds: seeds,
      lastAdded: { root: product.root, title: product.title, vendor: product.vendor },
      signature: (state.signature || '') + '+predict:' + product.root,
      /*  Always >= 1: this is the whole reason an empty-cart PDP still gets a
       *  precompute. The itemCount gate that suppresses work on EMPTY
       *  NON-PRODUCT pages is untouched. */
      itemCount: (state.itemCount || 0) + 1,
      predicted: true
    };
  }

  /*  Reads the PDP's own product from the recs container, which already
   *  carries everything needed. Returns null off-PDP. */
  function pdpProductForPrediction() {
    if (typeof document === 'undefined') { return null; }
    var el = document.querySelector('[data-mdv-recs="pdp"]');
    if (!el) { return null; }
    var ctx = readContext(el);
    if (!ctx.ownRoot) { return null; }
    return { root: ctx.ownRoot, title: ctx.ownTitle, vendor: ctx.ownVendor };
  }

  function precomputeForAdd() {
    var product = pdpProductForPrediction();
    if (!product) { return Promise.resolve(null); }

    return readCartState().then(function (state) {
      var predicted = predictedCartState(state, product);
      var ctx = ctxFromState(predicted);
      var key = seedKeyOf(predicted, ctx.anchorColourKey);

      /* already precomputed for this exact basket - nothing to do */
      if (cartPrecomputed && cartPrecomputed.seedKey === key) {
        return cartPrecomputed;
      }

      return computeCartTiles(predicted).then(function (built) {
        var entry = {
          seedKey: built.seedKey,
          tiles: built.tiles,
          ctx: built.ctx,
          ready: warmImages(tileImageUrls(built.tiles), IMAGE_READY_TIMEOUT_MS)
        };
        cartPrecomputed = entry;
        return entry;
      });
    })['catch'](function () { return null; });
  }

  /*  Paints the precomputed set immediately on add, then reconciles.
   *
   *  ACCEPTED EDGE: a failed add, or a multi-add such as Shop the Look,
   *  briefly shows recommendations for a basket that did not happen. The
   *  reconcile below corrects it within one cart round-trip. Wrong-then-
   *  corrected is the deliberate trade against blank-then-right. */
  function paintPrecomputedThenReconcile() {
    var el = cartContainer(null);
    if (!el) { return Promise.resolve(null); }

    var entry = cartPrecomputed;
    var reconcile = function () {
      cartRunId++;
      return runCart(el, cartRunId);
    };

    if (!entry || !entry.tiles || !entry.tiles.length) { return reconcile(); }

    cartRunId++;
    var myRun = cartRunId;
    return entry.ready.then(function () {
      if (myRun === cartRunId) {
        cartRenderedSeedKey = entry.seedKey;
        renderCartInto(el, entry.tiles, entry.ctx);
      }
      /*  Reconcile against the real cart. runCart's fast path makes this a
       *  no-op when the confirmed seed matches what was predicted. */
      return reconcile();
    })['catch'](function () { return reconcile(); });
  }

  /*  Narrow test for a real add-to-cart control. Kept tight so the head
   *  start never fires on unrelated clicks. */
  function isAddToCartControl(node) {
    if (!node || node.nodeType !== 1 || !node.getAttribute) { return false; }
    var name = node.getAttribute('name');
    if (name === 'add') { return true; }
    var id = node.getAttribute('id');
    if (id === 'add' || id === 'AddToCart') { return true; }
    var cls = node.className ? String(node.className) : '';
    return /(^|\s)(add-to-cart|add_to_cart|product-form__submit|quick-add)(\s|$)/.test(cls);
  }

  function scheduleCartRefresh() {
    if (typeof document === 'undefined') { return; }

    /*  COLD START: the debounce exists to coalesce bursts of mutations, not
     *  to delay the very first populate. On an add-to-cart the drawer opens
     *  immediately, so paying 300ms before even starting the fetch is felt.
     *  The first populate runs straight away; every later mutation debounces
     *  as before. */
    if (!cartEverPopulated) {
      mountCart(null, { force: false });
      return;
    }

    if (cartDebounceTimer) { clearTimeout(cartDebounceTimer); }
    cartDebounceTimer = setTimeout(function () {
      cartDebounceTimer = null;
      mountCart(null, { force: false });
    }, CART_DEBOUNCE_MS);
  }

  /*  Re-runs on cart mutation, debounced.
   *
   *  Two independent triggers, because the theme mutates the cart through
   *  jQuery AJAX while apps and other code paths may not:
   *    1. jQuery's global ajaxComplete, filtered to /cart URLs
   *    2. a MutationObserver on the drawer body, as a catch-all re-render
   *  Both funnel into the same debounce, and the signature check means a
   *  duplicate trigger is a no-op rather than a second row. */
  function watchCart() {
    if (cartWatching || typeof document === 'undefined') { return; }
    cartWatching = true;

    /*  ADD-CLICK HEAD START.
     *
     *  Capture phase, so it runs before the theme's own handlers and the
     *  recompute goes out IN PARALLEL with the cart POST rather than after
     *  it. On a PDP the precompute is usually already warm and this is a
     *  no-op; off-PDP (quick-add) it buys the whole round-trip.
     *
     *  Deliberately narrow: only real add-to-cart controls. */
    try {
      document.addEventListener('click', function (event) {
        var node = event.target;
        for (var i = 0; node && i < 6; i++) {
          if (isAddToCartControl(node)) { precomputeForAdd(); return; }
          node = node.parentNode;
        }
      }, true);
      document.addEventListener('submit', function (event) {
        var form = event.target;
        var action = form && form.getAttribute && form.getAttribute('action');
        if (action && action.indexOf('/cart/add') !== -1) { precomputeForAdd(); }
      }, true);
    } catch (e) { /* head start is an optimisation, never a requirement */ }

    try {
      var jq = (typeof window !== 'undefined') && (window.jQuery || window.$);
      if (jq && typeof jq(document).ajaxComplete === 'function') {
        jq(document).ajaxComplete(function (event, xhr, settings) {
          if (isCartUrl(settings && settings.url)) {
            /*  A cart mutation landed. Paint the precomputed set at once if
             *  we have one, then reconcile against the confirmed cart. */
            paintPrecomputedThenReconcile();
            /*  The basket changed, so any prediction built on the old basket
             *  is stale; rebuild it for the NEXT add. */
            cartPrecomputed = null;
            precomputeForAdd();
          }
        });
      }
    } catch (e) { /* jQuery absent or hostile - the observer still covers us */ }

    try {
      if (typeof MutationObserver === 'function') {
        var host = document.querySelector('.min-cart-main') ||
                   document.querySelector('.min-cart') ||
                   cartContainer(null);
        var row = cartContainer(null);
        if (host) {
          new MutationObserver(function (records) {
            /* Ignore our own writes into the row. */
            for (var i = 0; i < records.length; i++) {
              if (row && records[i].target === row) { return; }
            }
            scheduleCartRefresh();
          }).observe(host, { childList: true, subtree: true });
        }
      }
    } catch (e) { /* observer unavailable - ajaxComplete still covers us */ }
  }

  function autoInit() {
    if (typeof document === 'undefined') { return; }

    /*  Re-entry guard. The script has two include points:
     *    product-recommendations.liquid  - product templates
     *    header-min-cart.liquid          - every OTHER template
     *  The `unless mdv_tpl == 'product'` gate makes them mutually exclusive,
     *  so in the shipped configuration exactly one fires. This guard is
     *  belt-and-braces for the cases the gate cannot see - a page that
     *  renders both snippets, or a future third include point - because two
     *  <script> tags with the same src still execute twice and would
     *  double-register the cart watchers. First execution wins. */
    if (typeof window !== 'undefined') {
      if (window.__mdvRecsInit) { return; }
      window.__mdvRecsInit = 1;
    }

    var start = function () {
      var el = document.querySelector('[data-mdv-recs="pdp"]');

      /*  EAGER START (2026-08-22).
       *
       *  The PDP row's data work - Worker fetch, then the per-candidate
       *  product fetches - begins immediately. This script is deferred, so by
       *  the time it evaluates the DOM is parsed and the data- attributes it
       *  reads are already available.
       *
       *  Previously the whole run sat inside
       *  requestIdleCallback(run, { timeout: 2000 }), which meant that on a
       *  busy page the Worker request itself could be delayed by up to two
       *  seconds before it was even issued. The measured data path is ~150ms
       *  for the Worker and under a second for the product fetches, so that
       *  scheduling delay - not the data - dominated time-to-row.
       *
       *  Only the DOM write is still scheduled politely, inside mount(). */
      if (el) { mountPdp(el); }

      /*  The cart drawer is present on every page but is not visible on load,
       *  so its data work stays on the idle queue rather than competing with
       *  the PDP row for connections and main thread. Cart mutations trigger
       *  their own refresh regardless. */
      var cartEl = cartContainer(null);
      if (cartEl) {
        /*  Watching is two listener registrations and no I/O, so it is always
         *  wired immediately - a mutation must never be missed. */
        watchCart();

        /*  EAGER CART START, gated on a NON-EMPTY cart.
         *
         *  data-mdv-cart-count is rendered by Liquid from cart.item_count.
         *
         *    count > 0   populate now, alongside the PDP row. Previously this
         *                sat on requestIdleCallback(..., {timeout: 2000}), so
         *                the drawer row could lag ~2s + fetch behind load.
         *    count === 0 do NOTHING - no /cart.js, no Worker. This is what
         *                keeps idle non-PDP pageviews across six stores off
         *                the Worker now that the script is included
         *                site-wide. An add-to-cart mutation populates it.
         *    absent      treated as non-empty: correctness beats the saving.
         *
         *  A stale cached count of 0 self-heals: the theme's own addcart()
         *  runs on every pageload (custom.js) and writes the drawer, which
         *  trips the MutationObserver and triggers a populate with real data.
         */
        var cartCount = parseInt(cartEl.getAttribute('data-mdv-cart-count'), 10);
        if (isNaN(cartCount) || cartCount > 0) {
          mountCart(cartEl, { force: true });
        }

        /*  PDP: precompute the post-add drawer so the auto-open that follows
         *  add-to-cart paints from memory. Runs even on an empty cart - the
         *  predicted basket is {this product}, which is exactly the common
         *  first-add case. Off-PDP this returns immediately. */
        if (el) { precomputeForAdd(); }
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  }

  return {
    marker: MARKER,
    autoInit: autoInit,
    mountPdp: mountPdp,
    mountCart: mountCart,
    watchCart: watchCart,
    /* Exposed for unit tests and console diagnostics. */
    _internals: {
      parseSku: parseSku,
      styleRootOf: styleRootOf,
      colourwayRootOf: colourwayRootOf,
      normaliseColourWord: normaliseColourWord,
      colourKeyFromTitle: colourKeyFromTitle,
      resolveCandidateHandle: resolveCandidateHandle,
      ownProp: ownProp,
      storeKey: storeKey,
      toCents: toCents,
      normalizeProduct: normalizeProduct,
      firstAvailableVariant: firstAvailableVariant,
      markdownRatio: markdownRatio,
      passesMarkdownCap: passesMarkdownCap,
      buildFilter: buildFilter,
      bestSellerCollections: bestSellerCollections,
      buildTiles: buildTiles,
      fillFromBestSellers: fillFromBestSellers,
      readCartState: readCartState,
      isCartUrl: isCartUrl,
      schedulePaint: schedulePaint,
      mergeCandidateLists: mergeCandidateLists,
      getMergedRecs: getMergedRecs,
      cartTileHtml: cartTileHtml,
      renderCartInto: renderCartInto,
      runCart: runCart,
      resetCartState: function () {
        cartRunId = 0; cartLastSignature = null; cartWatching = false;
        cartEverPopulated = false; cartRenderedSeedKey = null; cartPrecomputed = null;
      },
      /*  The Worker cache is page-lifetime by design. Tests install a fresh
       *  fetch mock per case, which invalidates it, so the harness clears it
       *  between cases. */
      resetRecsCache: function () { recsPromiseCache = {}; },
      seedKeyOf: seedKeyOf,
      predictedCartState: predictedCartState,
      precomputeForAdd: precomputeForAdd,
      paintPrecomputedThenReconcile: paintPrecomputedThenReconcile,
      isAddToCartControl: isAddToCartControl,
      getRecsCached: getRecsCached,
      warmImages: warmImages,
      tileImageUrls: tileImageUrls,
      computeCartTiles: computeCartTiles,
      ctxFromState: ctxFromState,
      mapLimit: mapLimit,
      sizedImage: sizedImage,
      imageSrcset: imageSrcset,
      tileHtml: tileHtml,
      renderInto: renderInto,
      initSwiper: initSwiper,
      readContext: readContext,
      dedupeKey: dedupeKey,
      constants: {
        MAX_TILES: MAX_TILES,
        MIN_TILES: MIN_TILES,
        MAX_MARKDOWN: MAX_MARKDOWN,
        FETCH_TIMEOUT_MS: FETCH_TIMEOUT_MS,
        CONCURRENCY: CONCURRENCY,
        RECS_ENDPOINT: RECS_ENDPOINT,
        CART_MAX_TILES: CART_MAX_TILES,
        CART_MIN_TILES: CART_MIN_TILES,
        CART_DEBOUNCE_MS: CART_DEBOUNCE_MS,
        PAINT_TIMEOUT_MS: PAINT_TIMEOUT_MS,
        SEED_WEIGHT_LAST_ADDED: SEED_WEIGHT_LAST_ADDED,
        SEED_WEIGHT_OTHER: SEED_WEIGHT_OTHER,
        IMAGE_WIDTH: IMAGE_WIDTH,
        IMAGE_HEIGHT: IMAGE_HEIGHT,
        IMAGE_SRCSET_WIDTHS: IMAGE_SRCSET_WIDTHS,
        IMAGE_SIZES: IMAGE_SIZES
      }
    }
  };
});
